/**
 * OF-006 — TreatX Admin: Facility-media (clinic_profile) moderation queue.
 *
 * Mirrors the existing LogoModerationQueue authority but targets
 * media_assets rows with context_type='clinic_profile'. Single canonical
 * pipeline. No parallel collection. No shadow moderation surface.
 *
 *   Reader:  GET  /api/admin/clinic-media/pending
 *   Approve: POST /api/admin/clinic-media/{media_id}/approve
 *   Reject:  POST /api/admin/clinic-media/{media_id}/reject  { comment }
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../utils/api';
import { CheckCircle2, XCircle, RefreshCw, ImageIcon } from 'lucide-react';
// MEDIA-001 (2026-02-28): replaced the local axios `responseType:'blob'`
// AuthThumb implementation with the canonical AuthMediaThumb. The local
// version lacked AbortController, fallback variant chain, and deferred
// revoke — known causes of the CRA "Script error." overlay after a
// moderation approve→refetch race. No behaviour change for owners.
import AuthMediaThumb from '../../components/AuthMediaThumb';

export default function FacilityMediaModerationQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/clinic-media/pending');
      setItems(res.data?.items || []);
    } catch {
      toast.error('Failed to load pending facility media');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (media_id) => {
    try {
      await api.post(`/admin/clinic-media/${media_id}/approve`);
      toast.success('Approved');
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Approve failed';
      toast.error(msg);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    if (rejectComment.trim().length < 4) { toast.error('Comment must be at least 4 characters'); return; }
    try {
      await api.post(`/admin/clinic-media/${rejecting}/reject`, { comment: rejectComment.trim() });
      toast.success('Rejected');
      setRejecting(null);
      setRejectComment('');
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Reject failed';
      toast.error(msg);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="facility-moderation-queue">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Facility Photo Moderation</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and approve provider-uploaded facility/interior/building photos.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="fm-mod-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="text-xs text-slate-500" data-testid="fm-mod-count">
        {items.length} pending image{items.length === 1 ? '' : 's'}
      </div>

      {items.length === 0 && !loading && (
        <div className="text-center py-16 border-dashed border-2 border-slate-200 rounded-lg">
          <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Queue is empty.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((m) => (
          <Card key={m.media_id} data-testid={`fm-mod-row-${m.media_id}`}>
            <div className="aspect-video bg-slate-100">
              <AuthMediaThumb
                mediaId={m.media_id}
                alt=""
                variants={['preview', 'thumbnail', 'original']}
                testIdPrefix="fm-mod-thumb"
              />
            </div>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm" data-testid={`fm-mod-clinic-${m.media_id}`}>
                    {m.clinic_name || m.owner_id}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Uploaded {m.created_at && formatDateTimeLocale(m.created_at)}
                    {m.width && m.height && ` · ${m.width}×${m.height}`}
                  </div>
                </div>
                <Badge className="bg-amber-100 text-amber-800 text-[10px]">Pending</Badge>
              </div>

              {rejecting === m.media_id ? (
                <div className="space-y-2">
                  <Textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Reviewer note (visible to provider)"
                    rows={2}
                    data-testid={`fm-mod-reject-comment-${m.media_id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={reject}
                      data-testid={`fm-mod-reject-confirm-${m.media_id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Confirm reject
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setRejecting(null); setRejectComment(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => approve(m.media_id)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`fm-mod-approve-${m.media_id}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={() => { setRejecting(m.media_id); setRejectComment(''); }}
                    data-testid={`fm-mod-reject-${m.media_id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
